/**
 * Data transformation utilities for dashboard widgets
 */

export interface CodeSecurityData {
  data: {
    summary: {
      totalNumberOfRepositories: number;
      totalNumberOfIssues: number;
      data: Array<{
        integrationId: string;
        summary: {
          total: number;
          issues: {
            iac: {
              total: number;
              severity: {
                critical: number;
                high: number;
                medium: number;
                low: number;
                unknown: number;
              };
            };
            secrets: {
              total: number;
              severity: {
                critical: number;
                high: number;
                medium: number;
                low: number;
                unknown: number;
              };
            };
            vulnerabilities: {
              total: number;
              severity: {
                critical: number;
                high: number;
                medium: number;
                low: number;
                unknown: number;
              };
            };
          };
        };
      }>;
    };
  };
}

export interface TransformedCodeSecurityData {
  data: {
    summary: {
      data: {
        total: number;
        iac: {
          total: number;
          critical: number;
          high: number;
          medium: number;
          low: number;
        };
        vulnerabilities: {
          total: number;
          critical: number;
          high: number;
          medium: number;
          low: number;
        };
        secrets: {
          total: number;
          critical: number;
          high: number;
          medium: number;
          low: number;
        };
      };
    };
  };
}

/**
 * Transform code security data to match dashboard expectations
 */
export const transformCodeSecurityData = (
  rawData: CodeSecurityData,
): TransformedCodeSecurityData => {
  // Aggregate summary data across all integrations
  const aggregatedSummary = rawData.data.summary.data.reduce(
    (acc, integration) => {
      const issues = integration.summary.issues;

      return {
        iac: {
          total: acc.iac.total + issues.iac.total,
          critical: acc.iac.critical + issues.iac.severity.critical,
          high: acc.iac.high + issues.iac.severity.high,
          medium: acc.iac.medium + issues.iac.severity.medium,
          low: acc.iac.low + issues.iac.severity.low,
        },
        vulnerabilities: {
          total: acc.vulnerabilities.total + issues.vulnerabilities.total,
          critical:
            acc.vulnerabilities.critical +
            issues.vulnerabilities.severity.critical,
          high: acc.vulnerabilities.high + issues.vulnerabilities.severity.high,
          medium:
            acc.vulnerabilities.medium + issues.vulnerabilities.severity.medium,
          low: acc.vulnerabilities.low + issues.vulnerabilities.severity.low,
        },
        secrets: {
          total: acc.secrets.total + issues.secrets.total,
          critical: acc.secrets.critical + issues.secrets.severity.critical,
          high: acc.secrets.high + issues.secrets.severity.high,
          medium: acc.secrets.medium + issues.secrets.severity.medium,
          low: acc.secrets.low + issues.secrets.severity.low,
        },
      };
    },
    {
      iac: { total: 0, critical: 0, high: 0, medium: 0, low: 0 },
      vulnerabilities: { total: 0, critical: 0, high: 0, medium: 0, low: 0 },
      secrets: { total: 0, critical: 0, high: 0, medium: 0, low: 0 },
    },
  );

  // Keep metrics data as-is without transformation

  return {
    data: {
      summary: {
        data: {
          total: rawData.data.summary.totalNumberOfRepositories,
          ...aggregatedSummary,
        },
      },
    },
  };
};

/**
 * Type guard to check if data is CodeSecurityData
 */
export const isCodeSecurityData = (data: unknown): data is CodeSecurityData => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const dataObj = data as Record<string, unknown>;

  // Check for required top-level structure
  if (!dataObj.data || typeof dataObj.data !== 'object') {
    return false;
  }

  const dataData = dataObj.data as Record<string, unknown>;

  // Check for required fields
  if (
    !dataData.summary ||
    !dataData.repositories ||
    !dataData.codeIssuesMetrics
  ) {
    return false;
  }

  // Check summary structure
  const summary = dataData.summary as Record<string, unknown>;
  if (!summary.data || !Array.isArray(summary.data)) {
    return false;
  }

  // Check repositories structure
  const repositories = dataData.repositories as Record<string, unknown>;
  if (!repositories.data || !Array.isArray(repositories.data)) {
    return false;
  }

  // Check codeIssuesMetrics structure
  const codeIssuesMetrics = dataData.codeIssuesMetrics as Record<
    string,
    unknown
  >;
  if (!codeIssuesMetrics.data || !Array.isArray(codeIssuesMetrics.data)) {
    return false;
  }

  return true;
};

/**
 * Check if data needs transformation based on structure
 */
export const needsTransformation = (data: unknown): boolean => {
  if (!isCodeSecurityData(data)) {
    return false;
  }

  // Check if it has the old structure (integration-specific summaries)
  if (data.data.summary && typeof data.data.summary === 'object') {
    const summary = data.data.summary as Record<string, unknown>;
    return Array.isArray(summary.data);
  }

  return false;
};
